import {
  FEE_UNIT_BASIS_MEASUREMENT,
  FEE_UNIT_BASIS_UNIT,
  FEE_VALUE_BASES_BY_VALUE_TYPE,
  FEE_VALUE_BASIS,
  FEE_VALUE_TYPE,
  FEE_VALUE_BASIS_LABEL,
  type FeeUnitBasis,
  type FeeValueBasis,
  type FeeValueType,
} from '@shared/constants/fees'
import { formatMoney } from './currencyUtils'

const AED = 'AED'
const PERCENTAGE_DECIMALS = 2
const QUANTITY_DECIMALS = 2

/**
 * A fee value the way an admin reads it: `"4.00%"` for a rate of the basis,
 * `"AED 193.75 / sqm"` for a rate per area, `"AED 5,000.00"` for a flat amount.
 * The value arrives as a decimal string from the API, so the only conversion
 * here is for display.
 *
 * The unit is part of the figure, never dropped: `"AED 193.75"` on its own is
 * the annual cost of one square metre read as the annual cost of the property.
 */
export function formatFeeValue(
  value: string | number | null | undefined,
  valueType: FeeValueType,
  unitBasis: FeeUnitBasis | null = null,
): string {
  if (value === null || value === undefined || value === '') return '—'
  if (valueType === FEE_VALUE_TYPE.PERCENTAGE) {
    return `${Number(value).toFixed(PERCENTAGE_DECIMALS)}%`
  }
  const amount = formatMoney(AED, value)
  if (valueType === FEE_VALUE_TYPE.RATE_PER_UNIT && unitBasis !== null) {
    return `${amount} / ${FEE_UNIT_BASIS_UNIT[unitBasis]}`
  }
  return amount
}

/** Always an AED figure — net / VAT / gross amounts are money, never rates. */
export function formatFeeAmount(amount: string | number | null | undefined): string {
  if (amount === null || amount === undefined || amount === '') return '—'
  return formatMoney(AED, amount)
}

/** The measurement a rate is multiplied by, in its own unit: `"1,250.00 sqm"`. */
export function formatUnitQuantity(
  unitQuantity: string | number | null | undefined,
  unitBasis: FeeUnitBasis | null,
): string {
  if (unitQuantity === null || unitQuantity === undefined || unitQuantity === '') return '—'
  if (unitBasis === null) return Number(unitQuantity).toFixed(QUANTITY_DECIMALS)
  return `${Number(unitQuantity).toLocaleString('en-US', {
    minimumFractionDigits: QUANTITY_DECIMALS,
    maximumFractionDigits: QUANTITY_DECIMALS,
  })} ${FEE_UNIT_BASIS_UNIT[unitBasis]}`
}

/** The inputs a calculation line is built from — one fee, however it is expressed. */
export interface FeeCalculationParts {
  value: string | number | null | undefined
  valueType: FeeValueType
  valueBasis: FeeValueBasis | null
  unitBasis?: FeeUnitBasis | null
  unitQuantity?: string | null
}

/**
 * How the fee is arrived at, in one line: `"4.00% of purchase cost"`,
 * `"Flat AED 5,000.00"`, `"Flat AED 4,000.00 split by investment value"`, or
 * `"AED 193.75 / sqm × 1,250.00 sqm, split by rental income"`.
 *
 * A flat fee's basis is not a base to multiply by — it names the investor share
 * the amount is apportioned by, hence "split by" rather than "of". Only the two
 * apportioning bases read that way; `flat_only` has no split, and a property
 * override that turns a rate into a flat amount leaves the definition's
 * percentage-only basis behind, which the backend then charges whole.
 *
 * A percentage with no basis reads as a bare rate — the backend refuses that
 * combination, so it only shows up on legacy rows.
 */
export function formatFeeCalculation(parts: FeeCalculationParts): string {
  const { value, valueType, valueBasis, unitBasis = null, unitQuantity = null } = parts
  const formatted = formatFeeValue(value, valueType, unitBasis)
  const allowedBases = FEE_VALUE_BASES_BY_VALUE_TYPE[valueType] ?? []
  const isBasisRead = valueBasis !== null && allowedBases.includes(valueBasis)
  const split = isBasisRead ? `split by ${FEE_VALUE_BASIS_LABEL[valueBasis].toLowerCase()}` : ''

  if (valueType === FEE_VALUE_TYPE.RATE_PER_UNIT) {
    return formatRateCalculation(formatted, split, unitBasis, unitQuantity)
  }

  if (valueType !== FEE_VALUE_TYPE.PERCENTAGE) {
    if (!isBasisRead || valueBasis === FEE_VALUE_BASIS.FLAT_ONLY) return `Flat ${formatted}`
    return `Flat ${formatted} ${split}`
  }

  if (!isBasisRead) return formatted
  return `${formatted} of ${FEE_VALUE_BASIS_LABEL[valueBasis].toLowerCase()}`
}

/**
 * A rate with no quantity is not a fee yet — the property has no area recorded,
 * so the line says which measurement is missing rather than implying a figure.
 */
function formatRateCalculation(
  formattedRate: string,
  split: string,
  unitBasis: FeeUnitBasis | null,
  unitQuantity: string | null,
): string {
  if (unitQuantity === null) {
    const measurement = unitBasis === null ? 'area' : FEE_UNIT_BASIS_MEASUREMENT[unitBasis]
    return `${formattedRate} — no ${measurement} recorded`
  }
  const product = `${formattedRate} × ${formatUnitQuantity(unitQuantity, unitBasis)}`
  return split ? `${product}, ${split}` : product
}

export function formatVatRate(
  isVatApplicable: boolean,
  vatPercentage: string | number | null | undefined,
): string {
  if (!isVatApplicable) return 'Not applicable'
  if (vatPercentage === null || vatPercentage === undefined || vatPercentage === '') return 'VAT'
  return `${Number(vatPercentage).toFixed(PERCENTAGE_DECIMALS)}%`
}
